import { Check, Crown, CreditCard, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { startCheckout } from "@/lib/payments";

// Map UI package id → backend product_id
const PRODUCT_MAP = {
    "4_weeks": "coaching_4_weeks",
    "8_weeks": "coaching_8_weeks",
    "12_weeks": "coaching_12_weeks",
};

const Programs = () => {
    const { t, lang } = useLanguage();
    const navigate = useNavigate();
    const [busyId, setBusyId] = useState(null);

    const goToAnalysis = (packageId) => {
        // Persist selection so the Analyse page picks it up
        try {
            sessionStorage.setItem("ella:selected-package", packageId);
        } catch (e) {
            // ignore storage failures
        }
        navigate("/analyse");
    };

    const buyPackage = async (uiId) => {
        const productId = PRODUCT_MAP[uiId];
        if (!productId) return;
        setBusyId(uiId);
        try {
            await startCheckout({ productId, language: lang });
        } finally {
            setBusyId(null);
        }
    };

    return (
        <section
            id="programs"
            data-testid="programs-section"
            className="py-24 md:py-32 bg-ella-cream"
        >
            <div className="ella-container">
                <div className="max-w-2xl">
                    <span className="ella-overline" data-testid="programs-overline">
                        {t.programs.overline}
                    </span>
                    <h2
                        className="ella-headline text-4xl md:text-5xl lg:text-6xl mt-4 text-ella-ink"
                        data-testid="programs-title"
                    >
                        {t.programs.title}
                    </h2>
                    <p className="mt-6 text-lg text-ella-ink/70 font-light">
                        {t.programs.subtitle}
                    </p>
                </div>

                <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
                    {t.programs.packages.map((pkg, idx) => {
                        const highlighted = pkg.highlighted;
                        return (
                            <div
                                key={pkg.id}
                                data-testid={`program-card-${pkg.id}`}
                                className={`relative group flex flex-col transition-all duration-500 ${
                                    highlighted
                                        ? "ella-card-dark lg:scale-[1.04] z-10"
                                        : "ella-card"
                                }`}
                            >
                                {highlighted && (
                                    <div
                                        className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-4 py-1.5 bg-ella-gold text-white text-[10px] uppercase tracking-[0.25em] rounded-full shadow-md"
                                        data-testid="program-most-popular"
                                    >
                                        <Crown className="w-3 h-3" strokeWidth={1.5} />
                                        {t.programs.most_popular}
                                    </div>
                                )}

                                <div className="flex items-baseline justify-between">
                                    <span className={highlighted ? "text-[10px] uppercase tracking-[0.32em] text-ella-gold" : "ella-overline"}>
                                        {pkg.duration}
                                    </span>
                                    <span className={`text-xs font-sans ${highlighted ? "text-ella-cream/60" : "text-ella-muted"}`}>
                                        0{idx + 1}
                                    </span>
                                </div>

                                <h3 className={`font-display text-3xl md:text-4xl mt-4 ${highlighted ? "text-ella-cream" : "text-ella-ink"}`}>
                                    {pkg.name}
                                </h3>
                                <p className={`mt-3 italic font-light ${highlighted ? "text-ella-cream/70" : "text-ella-muted"}`}>
                                    {pkg.tagline}
                                </p>

                                <div className="mt-7 flex items-baseline gap-2">
                                    <span className={`font-display text-5xl md:text-6xl ${highlighted ? "text-ella-champagne" : "text-ella-ink"}`}>
                                        {pkg.price}
                                    </span>
                                    <span className="text-sm uppercase tracking-[0.22em] text-ella-gold-dark">
                                        {t.programs.currency}
                                    </span>
                                </div>

                                <ul className="mt-7 space-y-3.5 flex-1">
                                    {pkg.features.map((f, i) => (
                                        <li key={i} className={`flex items-start gap-3 text-sm leading-relaxed ${highlighted ? "text-ella-cream/85" : "text-ella-ink/80"}`}>
                                            <span className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${highlighted ? "bg-ella-gold/20" : "bg-ella-sand"}`}>
                                                <Check className={`w-3 h-3 ${highlighted ? "text-ella-gold" : "text-ella-ink"}`} strokeWidth={2} />
                                            </span>
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    onClick={() => buyPackage(pkg.id)}
                                    disabled={busyId === pkg.id}
                                    data-testid={`program-cta-buy-${pkg.id}`}
                                    className={`mt-8 w-full ${highlighted ? "btn-gold" : "btn-primary"} disabled:opacity-60 disabled:cursor-not-allowed`}
                                >
                                    {busyId === pkg.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                                    ) : (
                                        <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                                    )}
                                    {t.programs.cta_buy}
                                </button>
                                <button
                                    onClick={() => goToAnalysis(pkg.id)}
                                    data-testid={`program-cta-${pkg.id}`}
                                    className={`mt-2 w-full text-xs tracking-wide py-2 transition-colors ${highlighted ? "text-ella-cream/70 hover:text-ella-gold" : "text-ella-muted hover:text-ella-ink"}`}
                                >
                                    {t.programs.cta_apply_first} →
                                </button>

                                {/* Payment placeholders */}
                                <div className={`mt-5 pt-5 border-t flex items-center justify-center gap-3 text-xs ${highlighted ? "border-ella-line-dark text-ella-cream/50" : "border-ella-line text-ella-muted"}`}>
                                    <CreditCard className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    <span>Stripe</span>
                                    <span className={highlighted ? "text-ella-line-dark" : "text-ella-line"}>·</span>
                                    <span>Visa</span>
                                    <span className={highlighted ? "text-ella-line-dark" : "text-ella-line"}>·</span>
                                    <span>Mastercard</span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <p className="mt-10 text-center text-sm text-ella-muted italic max-w-xl mx-auto" data-testid="programs-payment-note">
                    {t.programs.payment_note}
                </p>
            </div>
        </section>
    );
};

export default Programs;
